import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API, getAuthHeaders } from "../context/AuthContext";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import StatsCards from "../components/StatsCards";
import ProfitChart from "../components/ProfitChart";
import WithdrawalQueue from "../components/WithdrawalQueue";
import SlaveAdminManager from "../components/SlaveAdminManager";
import TelegramSettings from "../components/TelegramSettings";
import EmailSettings from "../components/EmailSettings";
import TwoFASetup from "../components/TwoFASetup";
import AutoApproveSettings from "../components/AutoApproveSettings";
import AuditLog from "../components/AuditLog";
import AnalyticsPage from "../components/AnalyticsPage";
import ExportButtons from "../components/ExportButtons";
import MT5Approvals from "../components/MT5Approvals";
import SlaveActivity from "../components/SlaveActivity";
import AdminPasswordCard from "../components/AdminPasswordCard";
import Distributors, { DefaultReferralCodeCard } from "../components/Distributors";
import ReferralSettings from "../components/ReferralSettings";
import BridgeMaster from "../components/BridgeMaster";
import SignalDispatch from "../components/SignalDispatch";
import SystemStatus from "../components/SystemStatus";
import BinaryAdmin from "../components/BinaryAdmin";
import SubscriptionFinancials from "../components/SubscriptionFinancials";
import UserLookup from "../components/UserLookup";
import TDSReport from "../components/TDSReport";
import TDSOverviewCard from "../components/TDSOverviewCard";
import GSTReport from "../components/GSTReport";
import TradingViewAdmin from "../components/TradingViewAdmin";
import { Trash } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMaster = user?.role === "master_admin";
  const [activeTab, setActiveTab] = useState("overview");
  const [cleaning, setCleaning] = useState(false);

  const handleRemoveSeedData = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to remove all seeded demo data? This will clear all demo profits, withdrawals, and test metrics while keeping your admin login safe."
    );
    if (!confirmed) return;
    setCleaning(true);
    try {
      await axios.post(`${API}/admin/cleanup-seed`, {}, { headers: getAuthHeaders() });
      toast.success("All seed data removed successfully! Refreshing...");
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove seed data");
    } finally {
      setCleaning(false);
    }
  };

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/login", { replace: true });
    }
  }, [user, loading, navigate, location]);

  useEffect(() => {
    if (!user || isMaster) return;
    const perms = user.permissions || ["withdrawals", "mt5"];
    const masterOnly = ["overview", "employees", "audit-log", "analytics", "referral-settings", "system-status", "binary-tree", "tds-report", "gst-report", "tradingview"];
    const permMap = { withdrawals: "withdrawals", "mt5-approvals": "mt5", "bridge-master": "mt5", "signal-dispatch": "mt5" };
    const allowed =
      masterOnly.includes(activeTab)
        ? false
        : activeTab === "settings" || activeTab === "distributors" || activeTab === "user-lookup"
        ? true // slaves can view user lookup, distributors, and manage their password
        : !permMap[activeTab] || perms.includes(permMap[activeTab]);
    if (!allowed) {
      const fallback =
        (perms.includes("withdrawals") && "withdrawals") ||
        (perms.includes("mt5") && "mt5-approvals") ||
        "withdrawals";
      setActiveTab(fallback);
    }
  }, [user, isMaster, activeTab, setActiveTab]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0A0A0A" }}>
        <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0A" }} data-testid="dashboard">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      <div className="md:ml-[220px]">
        <Header />
        <main className="p-6 max-w-[1600px] mx-auto">
          {isMaster && (
            <div className="flex justify-end mb-4">
              <button
                onClick={handleRemoveSeedData}
                disabled={cleaning}
                data-testid="remove-seed-data-button"
                className="px-3 py-1.5 rounded-md text-xs font-medium border border-rose-500/30 text-rose-400 bg-rose-500/5 hover:bg-rose-500/10 hover:border-rose-500/50 transition-all disabled:opacity-50 flex items-center gap-1.5"
                title="Clears all seeded demo data while preserving admin logins and real accounts"
              >
                <Trash size={13} weight="bold" />
                {cleaning ? "Removing Seed Data..." : "Remove Seed Data"}
              </button>
            </div>
          )}

          {activeTab === "overview" && isMaster && (
            <div className="space-y-6" data-testid="overview-section">
              <StatsCards />
              <SubscriptionFinancials />
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <TDSOverviewCard onNavigate={setActiveTab} />
              </div>
              <div className="flex items-center justify-between">
                <div />
                <ExportButtons exportType="daily-profit" title="Daily Profit" />
              </div>
              <ProfitChart />
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <WithdrawalQueue />
              </div>
            </div>
          )}

          {activeTab === "withdrawals" && (
            <div data-testid="withdrawals-section">
              <WithdrawalQueue />
            </div>
          )}

          {activeTab === "tds-report" && isMaster && (
            <div data-testid="tds-report-tab">
              <TDSReport />
            </div>
          )}

          {activeTab === "gst-report" && isMaster && (
            <div data-testid="gst-report-tab">
              <GSTReport />
            </div>
          )}

          {activeTab === "tradingview" && isMaster && (
            <div data-testid="tradingview-tab">
              <TradingViewAdmin />
            </div>
          )}

          {activeTab === "analytics" && isMaster && (
            <div data-testid="analytics-section">
              <AnalyticsPage />
            </div>
          )}

          {activeTab === "mt5-approvals" && (
            <div data-testid="mt5-approvals-tab">
              <MT5Approvals />
            </div>
          )}

          {activeTab === "bridge-master" && (
            <div data-testid="bridge-master-tab">
              <BridgeMaster />
            </div>
          )}

          {activeTab === "signal-dispatch" && (
            <div data-testid="signal-dispatch-tab">
              <SignalDispatch />
            </div>
          )}

          {activeTab === "binary-tree" && (
            <div data-testid="binary-tree-tab">
              <BinaryAdmin />
            </div>
          )}

          {activeTab === "system-status" && isMaster && (
            <div data-testid="system-status-tab">
              <SystemStatus />
            </div>
          )}

          {activeTab === "employees" && isMaster && (
            <div data-testid="employees-section" className="space-y-4">
              <SlaveAdminManager />
              <SlaveActivity />
            </div>
          )}

          {activeTab === "distributors" && (
            <div data-testid="distributors-tab">
              <Distributors />
            </div>
          )}

          {activeTab === "referral-settings" && isMaster && (
            <div data-testid="referral-settings-tab">
              <ReferralSettings />
            </div>
          )}

          {activeTab === "user-lookup" && (
            <div data-testid="user-lookup-tab">
              <UserLookup />
            </div>
          )}

          {activeTab === "settings" && (
            <div className="space-y-6" data-testid="settings-section">
              <AdminPasswordCard />
              {isMaster && (
                <>
                  <DefaultReferralCodeCard />
                  <TwoFASetup />
                  <AutoApproveSettings />
                  <TelegramSettings />
                  <EmailSettings />
                </>
              )}
            </div>
          )}

          {activeTab === "audit-log" && isMaster && (
            <div data-testid="audit-log-section">
              <AuditLog />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
